import { BookingParams, BookingPosition, ConstraintLevel, ConstraintSummary } from '../types';

/**
 * Basisklasse für eine einzelne Buchungs-Validierungsregel. Jede Regel
 * entscheidet zuerst, ob sie für die Position/Buchung greift, und liefert
 * dann ein erfülltes/nicht erfülltes Ergebnis auf ihrem Level.
 */
export abstract class ConstraintRule {
  private readonly level: ConstraintLevel;

  /**
   * @param level Schweregrad, mit dem diese Regel ihre Ergebnisse markiert (blockierend oder Warnung).
   */
  constructor(level: ConstraintLevel) {
    this.level = level;
  }

  /**
   * Entscheidet, ob die Regel für diese Position und Buchung überhaupt geprüft werden muss.
   * @param position Zu prüfende Buchungsposition.
   * @param params Buchungsparameter mit Kontext und Adaptern.
   * @returns True, wenn anschliessend `check` ausgeführt werden soll.
   */
  public abstract needsCheck(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<boolean>;

  /**
   * Führt die eigentliche Prüfung durch und liefert das Ergebnis.
   * @param position Zu prüfende Buchungsposition.
   * @param params Buchungsparameter mit Kontext und Adaptern.
   * @returns Ergebnis der Prüfung als erfüllte oder nicht erfüllte Zusammenfassung.
   */
  public abstract check(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<ConstraintSummary>;

  /**
   * Liefert eine erfüllte Zusammenfassung auf dem Level der Regel.
   * @returns Erfüllte ConstraintSummary ohne Meldung.
   */
  protected satisfied(): ConstraintSummary {
    return { level: this.level, satisfied: true, message: '' };
  }

  /**
   * Liefert eine nicht erfüllte Zusammenfassung mit Meldung auf dem Level der Regel.
   * @param message Nutzerlesbare Begründung, warum die Regel verletzt ist.
   * @returns Nicht erfüllte ConstraintSummary mit der übergebenen Meldung.
   */
  protected unsatisfied(message: string): ConstraintSummary {
    return { level: this.level, satisfied: false, message };
  }
}
